import clsx from 'clsx';
import type { ReactElement } from 'react';
import { FormattedNumber } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { type Currencies, getPremiumData, selectFlexiForwardRfsPremiumData } from '@/App/tiles/FlexiForward/utils.ts';
import { IsInternalSales } from '@/App/tiles/Option/utils/IsInternalSales.tsx';
import { getFlexiForwardQuote } from '@/store/flexi-forward/helpers/rfs.ts';

export function PriceDisplay({
  currencies,
  precision,
  isInternalSales,
}: {
  currencies: Currencies;
  precision: number;
  isInternalSales: boolean;
}): ReactElement {
  const { displayedCurrency, premiumAmount, premiumInPercent } = useFlexiForwardSelector((flexiForward) => {
    const quote = getFlexiForwardQuote(flexiForward.properties.rfs);
    const premiumData = selectFlexiForwardRfsPremiumData(quote);

    const { currency1, currency2 } = currencies;

    const selectedDisplayedCurrency = flexiForward.properties.notionalCurrencyIndex === 1 ? currency1 : currency2;

    const isUpfrontCurrency2 = flexiForward.properties.upfrontCurrency === currency2;
    const premiumCurrencyIndex = isUpfrontCurrency2 ? 2 : 1;

    const { premiumAmount: selectedPremiumAmount, premiumInPercent: selectedPremiumInPercent } = getPremiumData(
      premiumData,
      premiumCurrencyIndex,
    );

    return {
      displayedCurrency: selectedDisplayedCurrency,
      premiumAmount: selectedPremiumAmount,
      premiumInPercent: selectedPremiumInPercent,
    };
  });

  return (
    <div className={clsx('align-self-end pe-48px', { 'pt-8px': isInternalSales })} data-e2e="major-text">
      <div className="d-flex flex-column gap-4px justify-content-end align-items-end monospace-numbers">
        <div>
          <span className="fs-30">
            <FormattedNumber value={Math.abs(premiumAmount)} minimumFractionDigits={precision} />
          </span>
          <span className="fs-21 ms-8px">{displayedCurrency}</span>
        </div>
        <IsInternalSales>
          <div className="fs-12">
            <FormattedNumber value={Math.abs(premiumInPercent)} minimumFractionDigits={4} />
            &nbsp;%&nbsp;{displayedCurrency}
          </div>
        </IsInternalSales>
      </div>
    </div>
  );
}
